"use client";

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { Checkbox } from "../ui/checkbox";
import { useFormSubmission } from "@/hooks/use-form-submission";
import { PhoneInput } from "../reui/phone-input";
import { useParams, useRouter } from "next/navigation";
import { Country } from "react-phone-number-input";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { formSchema, FormSchemaType } from "@/lib/form-schema";

const brochureFormSchema = formSchema.pick({
  fullName: true,
  contactNumber: true,
  email: true,
  serviceId: true,
  enquiryType: true,
  terms: true,
});

type TalkToLearningAdvisorFormProps = {
  heading?: string;
  subHeading?: string;
  submitLabel?: string;
  onSuccess?: () => void;
  className?: string;
  serviceId?: number | string;
};

export const TalkToLearningAdvisorForm = ({
  heading,
  subHeading = "Have any question?",
  submitLabel = "Submit",
  onSuccess,
  className,
  serviceId,
}: TalkToLearningAdvisorFormProps) => {
  const router = useRouter();
  const { country } = useParams<{ country: Country }>();
  const form = useForm<FormSchemaType>({
    resolver: zodResolver(brochureFormSchema),
    defaultValues: {
      fullName: "",
      contactNumber: "",
      email: "",
      serviceId: serviceId ? [serviceId] : [],
      enquiryType: "TALK_TO_LEARNING_ADVISOR",
      terms: false,
    },
  });

  const { submit, isSubmitting } = useFormSubmission({
    setError: form.setError,
    onSuccess: ({ name, message }) => {
      form.reset();
      sessionStorage.setItem("thankYouData", JSON.stringify({ name, message }));
      onSuccess?.();
      router.push("/thank-you-for-enquiry");
    },
  });

  async function onSubmit(data: FormSchemaType) {
    await submit(data);
  }
  return (
    <Card className={cn("ring-0", className)}>
      <CardHeader>
        <CardTitle>{heading ?? "Download Brochure"}</CardTitle>
        <CardDescription>{subHeading}</CardDescription>
      </CardHeader>
      <CardContent>
        <form id="download-brochure-form" onSubmit={form.handleSubmit(onSubmit)}>
          <FieldGroup>
            <Controller
              name="fullName"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="talk-to-advisor-fullName">Full Name</FieldLabel>
                  <Input
                    {...field}
                    id="talk-to-advisor-fullName"
                    aria-invalid={fieldState.invalid}
                    placeholder="Please enter your full name"
                    autoComplete="off"
                    required
                  />
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
            <Controller
              name="contactNumber"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="talk-to-advisor-contactNumber">Phone</FieldLabel>
                  <PhoneInput
                    defaultCountry={country ? (country.toUpperCase() as Country) : "US"}
                    international
                    {...field}
                    id="talk-to-advisor-contactNumber"
                    aria-invalid={fieldState.invalid}
                    placeholder="Please enter your mobile number"
                    autoComplete="off"
                    required
                  />
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="talk-to-advisor-email">Email ID</FieldLabel>
                  <Input
                    {...field}
                    id="talk-to-advisor-email"
                    aria-invalid={fieldState.invalid}
                    placeholder="Please enter your email ID"
                    autoComplete="off"
                    required
                  />
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
            <Controller
              name="terms"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid} orientation="horizontal">
                  <Checkbox
                    id="talk-to-advisor-terms"
                    name={field.name}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <FieldLabel htmlFor="talk-to-advisor-terms" className="font-normal">
                    <span>
                      I agree to Spoclearn{" "}
                      <Link href={"/terms-and-conditions"} className="text-primary font-semibold">
                        terms of use
                      </Link>{" "}
                      and privacy policy
                    </span>
                  </FieldLabel>
                  {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
                </Field>
              )}
            />
          </FieldGroup>
        </form>
      </CardContent>
      <CardFooter className="bg-card border-0">
        <Field orientation="horizontal" className="w-full">
          <Button
            type="submit"
            disabled={isSubmitting}
            onClick={form.handleSubmit(onSubmit)}
            className="w-full"
          >
            {isSubmitting ? "Initiating Call..." : submitLabel}
          </Button>
        </Field>
      </CardFooter>
    </Card>
  );
};
